/**
 * Retry utility with exponential backoff
 *
 * Useful for handling race conditions and transient failures
 * in API calls.
 */

export interface RetryOptions {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries: number;
  /** Initial delay in milliseconds before first retry (default: 200) */
  initialDelayMs: number;
  /** Maximum delay in milliseconds between retries (default: 2000) */
  maxDelayMs: number;
  /** Multiplier for exponential backoff (default: 2) */
  backoffMultiplier: number;
  /** Optional condition to determine if error should trigger retry */
  retryCondition?: (error: unknown) => boolean;
}

const DEFAULT_RETRY_OPTIONS: RetryOptions = {
  maxRetries: 3,
  initialDelayMs: 200,
  maxDelayMs: 2000,
  backoffMultiplier: 2,
};

/**
 * Retry a function with exponential backoff
 *
 * @param fn - The async function to retry
 * @param options - Retry configuration options
 * @returns The result of the function if successful
 * @throws The last error if all retries fail
 *
 * @example
 * ```typescript
 * // Basic usage
 * const result = await retryWithBackoff(() => fetchData());
 *
 * // With custom options
 * const result = await retryWithBackoff(
 *   () => fetchUserProfile(userId),
 *   {
 *     maxRetries: 5,
 *     initialDelayMs: 100,
 *     retryCondition: (error) => error.status === 404,
 *   }
 * );
 * ```
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: Partial<RetryOptions> = {}
): Promise<T> {
  const config = { ...DEFAULT_RETRY_OPTIONS, ...options };
  let lastError: unknown;
  let delay = config.initialDelayMs;

  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: unknown) {
      lastError = error;

      // Check if we should retry this error
      if (config.retryCondition && !config.retryCondition(error)) {
        throw error;
      }

      // Don't wait after the last attempt
      if (attempt < config.maxRetries) {
        console.log(`Retry attempt ${attempt + 1}/${config.maxRetries} after ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        delay = Math.min(delay * config.backoffMultiplier, config.maxDelayMs);
      }
    }
  }

  throw lastError;
}
